import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { amplifyConfigured } from '@/lib/amplify-ready';
import type { ListingSurfaceKey, PlatformFeatureFlagKey } from '@shopgnd/shared';
import { DEFAULT_LISTING_SURFACES, DEFAULT_PLATFORM_FEATURE_FLAGS } from '@shopgnd/shared';
import { getPlatformConfigSnapshot } from '@/api/platformConfig';
import type { PlatformSettings } from '@/api/platformSettings';
import { hubQueryKeys } from '@/lib/hubQueryClient';
import { getRuntimeSurface } from '@/lib/platformSurfaceConfig';
import { getRuntimePlanDefinitions } from '@/lib/platformPlanConfig';
import { getRuntimeFeatureFlags, isRuntimeFeatureEnabled } from '@/lib/platformFeatureFlagsRuntime';

export type PlatformConfigValue = {
  plans: ReturnType<typeof getRuntimePlanDefinitions>;
  surfaces: typeof DEFAULT_LISTING_SURFACES;
  featureFlags: typeof DEFAULT_PLATFORM_FEATURE_FLAGS;
  settings: PlatformSettings | undefined;
  ready: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
};

const PlatformConfigContext = createContext<PlatformConfigValue | null>(null);

function usePlatformConfigQuery(): PlatformConfigValue {
  const [amplifyReady, setAmplifyReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void amplifyConfigured.then(() => {
      if (!cancelled) setAmplifyReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const query = useQuery({
    queryKey: hubQueryKeys.platformConfig,
    queryFn: getPlatformConfigSnapshot,
    staleTime: 60_000,
    refetchOnMount: 'always',
    enabled: amplifyReady,
  });

  const plans = query.isSuccess
    ? Array.isArray(query.data.plans)
      ? query.data.plans
      : getRuntimePlanDefinitions()
    : query.isLoading
      ? []
      : getRuntimePlanDefinitions();
  const surfaces = query.data?.surfaces ?? DEFAULT_LISTING_SURFACES;
  const featureFlags = query.data?.featureFlags ?? getRuntimeFeatureFlags();

  return {
    plans,
    surfaces,
    featureFlags,
    settings: query.data?.settings,
    ready: query.isSuccess,
    isLoading: query.isLoading || query.isFetching,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

/** Loads platform config once for the app tree (avoids duplicate useQuery / hook queue issues). */
export function PlatformConfigProvider({ children }: { children: ReactNode }) {
  const value = usePlatformConfigQuery();
  return <PlatformConfigContext.Provider value={value}>{children}</PlatformConfigContext.Provider>;
}

export function usePlatformConfig(): PlatformConfigValue {
  const ctx = useContext(PlatformConfigContext);
  if (!ctx) {
    throw new Error('usePlatformConfig must be used within PlatformConfigProvider');
  }
  return ctx;
}

export function usePlanDefinitions() {
  const { plans, ready, isLoading, refetch } = usePlatformConfig();
  return { plans, ready, isLoading, refetch };
}

export function useSurfaceConfig(key: ListingSurfaceKey) {
  const { surfaces, ready } = usePlatformConfig();
  if (ready) {
    return (
      surfaces.find((s) => s.surfaceKey === key) ??
      DEFAULT_LISTING_SURFACES.find((s) => s.surfaceKey === key)!
    );
  }
  return getRuntimeSurface(key);
}

export function useFeatureFlag(key: PlatformFeatureFlagKey): boolean {
  const { featureFlags, ready } = usePlatformConfig();
  if (ready) {
    return featureFlags[key] !== false;
  }
  return isRuntimeFeatureEnabled(key);
}
